import React from 'react';
import { WidgetType } from '../../types/widget';
import {
  Analytics,
  TrendingUp,
  TimerOff,
  Summarize,
  Policy,
  Timeline,
  AccountBalanceWallet,
  ViewWeek,
  Gavel,
  People,
  WbSunny,
  BarChart,
  Notes,
  Description,
  Folder,
  Speed,
} from '@mui/icons-material';

export interface WidgetRegistration {
  type: WidgetType;
  title: string;
  description: string;
  icon: React.ElementType;
  defaultSize: { width: number; height: number; minWidth: number; minHeight: number };
}

// Using Partial to allow some widget types to be excluded from the selector
export const widgetRegistry: Partial<Record<WidgetType, WidgetRegistration>> = {
  [WidgetType.TIMING_TOWER]: {
    type: WidgetType.TIMING_TOWER,
    title: 'Timing Tower',
    description:
      'A vertical, real-time summary of the entire production schedule, progress, and daily details.',
    icon: ViewWeek,
    defaultSize: { width: 3, height: 10, minWidth: 3, minHeight: 6 },
  },
  [WidgetType.SCENE_ANALYSIS]: {
    type: WidgetType.SCENE_ANALYSIS,
    title: 'Scene Analysis',
    description:
      'Tracks daily planned vs. actual pages, scenes, and setups to identify discrepancies.',
    icon: Analytics,
    defaultSize: { width: 8, height: 6, minWidth: 4, minHeight: 3 },
  },
  [WidgetType.DRIFT_ANALYSIS]: {
    type: WidgetType.DRIFT_ANALYSIS,
    title: 'Drift Analysis',
    description:
      'Monitors production drift against the original schedule to see if you are ahead or behind.',
    icon: TrendingUp,
    defaultSize: { width: 4, height: 6, minWidth: 3, minHeight: 3 },
  },
  [WidgetType.DELAY_ANALYSIS]: {
    type: WidgetType.DELAY_ANALYSIS,
    title: 'Delay Analysis',
    description:
      'Analyzes the frequency, duration, and reasons for on-set delays to identify patterns.',
    icon: TimerOff,
    defaultSize: { width: 6, height: 5, minWidth: 4, minHeight: 4 },
  },
  // PRODUCTION_SUMMARY removed - functionality covered by other widgets
  [WidgetType.PRODUCTION_DAILY_FOCUS]: {
    type: WidgetType.PRODUCTION_DAILY_FOCUS,
    title: 'Daily Focus',
    description:
      'Highlights critical risks for the current shoot day, including turnaround, delays, and scene complexity.',
    icon: Policy,
    defaultSize: { width: 5, height: 5, minWidth: 4, minHeight: 4 },
  },
  [WidgetType.BUDGET_TRACKING]: {
    type: WidgetType.BUDGET_TRACKING,
    title: 'Budget Tracking',
    description:
      'Monitors daily "hot costs" and overall budget performance against the original plan.',
    icon: AccountBalanceWallet,
    defaultSize: { width: 6, height: 6, minWidth: 4, minHeight: 4 },
  },
  [WidgetType.PRODUCTION_TIMELINE]: {
    type: WidgetType.PRODUCTION_TIMELINE,
    title: 'Production Timeline',
    description:
      'A visual Gantt chart of the entire production schedule, from prep to wrap.',
    icon: Timeline,
    defaultSize: { width: 12, height: 4, minWidth: 6, minHeight: 3 },
  },
  [WidgetType.PRODUCTION_ASSUMPTIONS]: {
    type: WidgetType.PRODUCTION_ASSUMPTIONS,
    title: 'Production Assumptions',
    description:
      'Displays the original baseline assumptions (from locked budget & schedule) for comparison.',
    icon: Gavel,
    defaultSize: { width: 5, height: 5, minWidth: 4, minHeight: 3 },
  },
  [WidgetType.CAST_SCHEDULE]: {
    type: WidgetType.CAST_SCHEDULE,
    title: 'Cast Schedule',
    description:
      'Tracks principal cast availability, call times, and conflicts across the production calendar.',
    icon: People,
    defaultSize: { width: 6, height: 5, minWidth: 4, minHeight: 4 },
  },
  [WidgetType.WEATHER_MONITOR]: {
    type: WidgetType.WEATHER_MONITOR,
    title: 'Weather Monitor',
    description:
      'Displays weather outlooks for upcoming shoot days to anticipate delays and contingency plans.',
    icon: WbSunny,
    defaultSize: { width: 4, height: 4, minWidth: 3, minHeight: 3 },
  },
  [WidgetType.CUSTOM_CHART]: {
    type: WidgetType.CUSTOM_CHART,
    title: 'Custom Chart',
    description:
      'Embed bespoke data visualizations such as KPIs, trend lines, or burn-down charts.',
    icon: BarChart,
    defaultSize: { width: 6, height: 4, minWidth: 4, minHeight: 3 },
  },
  [WidgetType.TEXT_DISPLAY]: {
    type: WidgetType.TEXT_DISPLAY,
    title: 'Text Display',
    description:
      'Share production notes, announcements, or updates in a simple rich-text panel.',
    icon: Notes,
    defaultSize: { width: 4, height: 3, minWidth: 3, minHeight: 2 },
  },
  [WidgetType.DOCUMENT_STATUS]: {
    type: WidgetType.DOCUMENT_STATUS,
    title: 'Document Status',
    description:
      'Tracks receipt and timeliness of Call Sheets, DPRs, and Hot Costs for each shoot day.',
    icon: Description,
    defaultSize: { width: 4, height: 5, minWidth: 3, minHeight: 4 },
  },
  [WidgetType.DOCUMENT_LIBRARY]: {
    type: WidgetType.DOCUMENT_LIBRARY,
    title: 'Document Library',
    description:
      'Browse, search, preview and download all production documents organized by type.',
    icon: Folder,
    defaultSize: { width: 4, height: 6, minWidth: 3, minHeight: 5 },
  },
  [WidgetType.PRODUCTION_VELOCITY]: {
    type: WidgetType.PRODUCTION_VELOCITY,
    title: 'Production Velocity',
    description:
      'Tracks cumulative pages shot vs. planned across all schedule versions with a projected wrap trajectory.',
    icon: Speed,
    defaultSize: { width: 8, height: 5, minWidth: 6, minHeight: 4 },
  },
};

